import CashTamerHttpClient from './CashTamerHttpClient';
import CashTamerApiBase from './CashTamerApiBase';
import BankAccountDashboardResponseModel from '@/models/bankaccounts/BankAccountDashboardResponseModel';
import BankAccountAdjustBalanceRequestModel from '@/models/bankaccounts/BankAccountAdjustBalanceRequestModel';
import BankAccountChangeNameRequestModel from '@/models/bankaccounts/BankAccountChangeNameRequestModel';
import BankAccountChangeNotesRequestModel from '@/models/bankaccounts/BankAccountChangeNotesRequestModel';
import BankAccountCreateFundRequestModel from '@/models/bankaccounts/BankAccountCreateFundRequestModel';
import BankAccountCreateMemberRequestModel from '@/models/bankaccounts/BankAccountCreateMemberRequestModel';
import BankAccountChangeActualBankBalanceRequestModel from '@/models/bankaccounts/BankAccountChangeActualBankBalanceRequestModel';
import BankAccountDepositRequestModel from '@/models/bankaccounts/BankAccountDepositRequestModel';
import BankAccountLogResponseModel from '@/models/bankaccounts/BankAccountLogResponseModel';
import BankAccountMemberResponseModel from '@/models/bankaccounts/BankAccountMemberResponseModel';
import BankAccountTransactionResponseModel from '@/models/bankaccounts/BankAccountTransactionResponseModel';
import BankAccountObligateRequestModel from '@/models/bankaccounts/BankAccountObligateRequestModel';
import BankAccountObligateResponseModel from '@/models/bankaccounts/BankAccountObligateResponseModel';
import BankAccountPurchaseFundsRequestModel from '@/models/bankaccounts/BankAccountPurchaseFundsRequestModel';
import BankAccountPurchaseTransactionsRequestModel from '@/models/bankaccounts/BankAccountPurchaseTransactionsRequestModel';
import BankAccountTransferRequestModel from '@/models/bankaccounts/BankAccountTransferRequestModel';
import BankAccountFundResponseModel from '@/models/bankaccounts/BankAccountFundResponseModel';
import BankAccountAutomaticBackupResponseModel from '@/models/bankaccounts/BankAccountAutomaticBackupResponseModel';
import BankAccountFundAutomaticBackupResponseModel from '@/models/bankaccounts/BankAccountFundAutomaticBackupResponseModel';

export default class BankAccountsApi extends CashTamerApiBase {


    public async adjustBalanceAsync(bankAccountId: number, jwt: string, request: BankAccountAdjustBalanceRequestModel): Promise<void> {

        try {
            const httpClient = new CashTamerHttpClient();

            const url: string = 'member/bankaccounts/' + bankAccountId + '/adjustbalance';
            await httpClient.securePostAsync(url, request, jwt);
        }
        catch ( err ) {
            return this.handleApiError(err);
        }
    }

    public async changeActualBankBalanceAsync(bankAccountId: number, jwt: string, request: BankAccountChangeActualBankBalanceRequestModel): Promise<void> {

        try {
            const httpClient = new CashTamerHttpClient();

            const url: string = 'member/bankaccounts/' + bankAccountId + '/actualbankbalance';
            await httpClient.securePutAsync(url, request, jwt);
        }
        catch ( err ) {
            return this.handleApiError(err);
        }
    }

    public async changeNameAsync(bankAccountId: number, jwt: string, request: BankAccountChangeNameRequestModel): Promise<void> {

        try {
            const httpClient = new CashTamerHttpClient();

            const url: string = 'member/bankaccounts/' + bankAccountId + '/name';
            await httpClient.securePutAsync(url, request, jwt);
        }
        catch ( err ) {
            return this.handleApiError(err);
        }
    }

    public async changeNotesAsync(bankAccountId: number, jwt: string, request: BankAccountChangeNotesRequestModel): Promise<void> {

        try {
            const httpClient = new CashTamerHttpClient();

            const url: string = 'member/bankaccounts/' + bankAccountId + '/notes';
            await httpClient.securePutAsync(url, request, jwt);
        }
        catch ( err ) {
            return this.handleApiError(err);
        }
    }

    public async createFundAsync(bankAccountId: number, jwt: string, request: BankAccountCreateFundRequestModel): Promise<void> {

        try {
            const httpClient = new CashTamerHttpClient();

            const url: string = 'member/bankaccounts/' + bankAccountId + '/funds';
            await httpClient.securePostAsync(url, request, jwt);
        }
        catch ( err ) {
            return this.handleApiError(err);
        }
    }

    public async createMemberAsync(bankAccountId: number, jwt: string, request: BankAccountCreateMemberRequestModel): Promise<void> {

        try {
            const httpClient = new CashTamerHttpClient();

            const url: string = 'member/bankaccounts/' + bankAccountId + '/members';
            await httpClient.securePostAsync(url, request, jwt);
        }
        catch ( err ) {
            return this.handleApiError(err);
        }
    }

    public async deleteAsync(bankAccountId: number, jwt: string): Promise<void> {

        try {
            const httpClient = new CashTamerHttpClient();

            const url: string = 'member/bankaccounts/' + bankAccountId;
            await httpClient.secureDeleteAsync(url, jwt);
        }
        catch ( err ) {
            return this.handleApiError(err);
        }
    }

    public async depositAsync(bankAccountId: number, jwt: string, request: BankAccountDepositRequestModel): Promise<void> {

        try {
            const httpClient = new CashTamerHttpClient();

            const url: string = 'member/bankaccounts/' + bankAccountId + '/deposit';
            await httpClient.securePostAsync(url, request, jwt);
        }
        catch ( err ) {
            return this.handleApiError(err);
        }
    }

    public async getAutomaticBackupAsync(bankAccountId: number, jwt: string): Promise<BankAccountAutomaticBackupResponseModel> {

        try {
            const httpClient = new CashTamerHttpClient();

            const url: string = 'member/bankaccounts/' + bankAccountId + '/backups/automatic';
            const httpResponse = await httpClient.secureGetAsync(url, jwt);

            const backup = new BankAccountAutomaticBackupResponseModel();
            backup.hasBackup = httpResponse.data.hasBackup;

            if (backup.hasBackup) {
                backup.availableFunds = httpResponse.data.availableFunds;
                backup.availableTransactions = httpResponse.data.availableTransactions;
                backup.backupDateTimeUtc = httpResponse.data.backupDateTimeUtc;
                backup.balance = httpResponse.data.balance;
                backup.bankAccountName = httpResponse.data.bankAccountName;
                backup.credits = httpResponse.data.credits;
                backup.debits = httpResponse.data.debits;

                const len: number = httpResponse.data.funds.length - 1;

                for ( let i = 0; i <= len; i++ ) {
                    const fund: BankAccountFundAutomaticBackupResponseModel = new BankAccountFundAutomaticBackupResponseModel();
                    fund.balance = httpResponse.data.funds[i].balance;
                    fund.credits = httpResponse.data.funds[i].credits;
                    fund.debits = httpResponse.data.funds[i].debits;
                    fund.fundId = httpResponse.data.funds[i].fundId;
                    fund.fundName = httpResponse.data.funds[i].fundName;

                    backup.funds.push(fund);
                }
            }

            return backup;
        }
        catch (err) {
            return this.handleApiError(err);
        }
    }

    public async getManualBackupAsync(bankAccountId: number, jwt: string): Promise<string[]> {

        try {
            const httpClient = new CashTamerHttpClient();

            const url: string = 'member/bankaccounts/' + bankAccountId + '/backups/manual';
            const httpResponse = await httpClient.secureGetAsync(url, jwt);
            return httpResponse.data;
        }
        catch (err) {
            return this.handleApiError(err);
        }
    }

    public async getBankAccountDashBoardAsync(bankAccountId: number, jwt: string): Promise<BankAccountDashboardResponseModel> {

        try {
            const httpClient = new CashTamerHttpClient();

            const url: string = 'member/bankaccounts/' + bankAccountId;
            const httpResponse = await httpClient.secureGetAsync(url, jwt);

            const bankAccount = new BankAccountDashboardResponseModel();
            bankAccount.actualBankBalance = httpResponse.data.actualBankBalance;
            bankAccount.actualBankBalanceAsOfDate = httpResponse.data.actualBankBalanceAsOfDate;
            bankAccount.actualBankBalanceDifference = httpResponse.data.actualBankBalanceDifference;
            bankAccount.authorizedOperations = httpResponse.data.authorizedOperations;
            bankAccount.availableFunds = httpResponse.data.availableFunds;
            bankAccount.availableTransactions = httpResponse.data.availableTransactions;
            bankAccount.balance = httpResponse.data.balance;
            bankAccount.bankAccountId = httpResponse.data.bankAccountId;
            bankAccount.bankAccountName = httpResponse.data.bankAccountName;
            bankAccount.createdBy = httpResponse.data.createdBy;
            bankAccount.createdDateTimeUtc = httpResponse.data.createdDateTimeUtc;
            bankAccount.credits = httpResponse.data.credits;
            bankAccount.debits = httpResponse.data.debits;
            bankAccount.notes = httpResponse.data.notes;
            bankAccount.totalFundBalance = httpResponse.data.totalFundBalance;
            bankAccount.totalFundBalanceDifference = httpResponse.data.totalFundBalanceDifference;

            const len: number = httpResponse.data.funds.length - 1;

            for ( let i = 0; i <= len; i++ ) {
                const fund: BankAccountFundResponseModel = new BankAccountFundResponseModel();
                fund.fundId = httpResponse.data.funds[i].fundId;
                fund.fundName = httpResponse.data.funds[i].fundName;
                fund.balance = httpResponse.data.funds[i].balance;
                fund.isDepositFund = httpResponse.data.funds[i].isDepositFund;

                bankAccount.funds.push(fund);
            }

            return bankAccount;
        }
        catch (err) {
            return this.handleApiError(err);
        }
    }

    public async getLogsAsync(bankAccountId: number, jwt: string): Promise<BankAccountLogResponseModel[]> {

        try {
            const httpClient = new CashTamerHttpClient();

            const url: string = 'member/bankaccounts/' + bankAccountId + '/logs';
            const httpResponse = await httpClient.secureGetAsync(url, jwt);

            const values = new Array<BankAccountLogResponseModel>();

            const len: number = httpResponse.data.length - 1;

            for ( let i = 0; i <= len; i++ ) {
                const value: BankAccountLogResponseModel = new BankAccountLogResponseModel();
                value.fieldName = httpResponse.data[i].fieldName;
                value.oldValue = httpResponse.data[i].oldValue;
                value.newValue = httpResponse.data[i].newValue;
                value.createdDateTimeUtc = httpResponse.data[i].createdDateTimeUtc;
                value.createdBy = httpResponse.data[i].createdBy;

                values.push(value);
            }

            return values;
        }
        catch (err) {
            return this.handleApiError(err);
        }
    }

    public async getMembersAsync(bankAccountId: number, jwt: string): Promise<BankAccountMemberResponseModel[]> {

        try {
            const httpClient = new CashTamerHttpClient();

            const url: string = 'member/bankaccounts/' + bankAccountId + '/members';
            const httpResponse = await httpClient.secureGetAsync(url, jwt);

            const values = new Array<BankAccountMemberResponseModel>();

            const len: number = httpResponse.data.length - 1;

            for ( let i = 0; i <= len; i++ ) {
                const value: BankAccountMemberResponseModel = new BankAccountMemberResponseModel();
                value.bankAccountMemberId = httpResponse.data[i].bankAccountMemberId;
                value.givenName = httpResponse.data[i].givenName;
                value.surname = httpResponse.data[i].surname;
                value.roleType = httpResponse.data[i].roleType;

                values.push(value);
            }

            return values;
        }
        catch (err) {
            return this.handleApiError(err);
        }
    }

    public async getTransactionsAsync(bankAccountId: number, jwt: string): Promise<BankAccountTransactionResponseModel[]> {

        try {
            const httpClient = new CashTamerHttpClient();

            const url: string = 'member/bankaccounts/' + bankAccountId + '/transactions';
            const httpResponse = await httpClient.secureGetAsync(url, jwt);

            const values = new Array<BankAccountTransactionResponseModel>();

            const len: number = httpResponse.data.length - 1;

            for ( let i = 0; i <= len; i++ ) {
                const value: BankAccountTransactionResponseModel = new BankAccountTransactionResponseModel();
                value.transactionId = httpResponse.data[i].transactionId;
                value.fundId = httpResponse.data[i].fundId;
                value.fundName = httpResponse.data[i].fundName;
                value.debit = httpResponse.data[i].debit;
                value.credit = httpResponse.data[i].credit;
                value.transactionDate = httpResponse.data[i].transactionDate;
                value.transactionDescription = httpResponse.data[i].transactionDescription;

                values.push(value);
            }

            return values;
        }
        catch (err) {
            return this.handleApiError(err);
        }
    }

    public async obligateAsync(bankAccountId: number, jwt: string, request: BankAccountObligateRequestModel): Promise<BankAccountObligateResponseModel> {

        try {
            const httpClient = new CashTamerHttpClient();

            const url: string = 'member/bankaccounts/' + bankAccountId + '/obligate';
            const httpResponse = await httpClient.securePostAsync(url, request, jwt);

            const obligateResponse = new BankAccountObligateResponseModel();
            obligateResponse.depositFundBalance = httpResponse.data.depositFundBalance;
            obligateResponse.obligationFundBalance = httpResponse.data.obligationFundBalance;
            obligateResponse.obligationFundId = httpResponse.data.obligationFundId;

            return obligateResponse;
        }
        catch ( err ) {
            return this.handleApiError(err);
        }
    }

    public async purchaseFundsAsync(bankAccountId: number, jwt: string, request: BankAccountPurchaseFundsRequestModel): Promise<void> {

        try {
            const httpClient = new CashTamerHttpClient();

            const url: string = 'member/bankaccounts/' + bankAccountId + '/purchasefunds';
            await httpClient.securePostAsync(url, request, jwt);
        }
        catch ( err ) {
            return this.handleApiError(err);
        }
    }

    public async purchaseTransactionsAsync(bankAccountId: number, jwt: string, request: BankAccountPurchaseTransactionsRequestModel): Promise<void> {

        try {
            const httpClient = new CashTamerHttpClient();

            const url: string = 'member/bankaccounts/' + bankAccountId + '/purchasetransactions';
            await httpClient.securePostAsync(url, request, jwt);
        }
        catch ( err ) {
            return this.handleApiError(err);
        }
    }

    public async transferAsync(bankAccountId: number, jwt: string, request: BankAccountTransferRequestModel): Promise<void> {

        try {
            const httpClient = new CashTamerHttpClient();

            const url: string = 'member/bankaccounts/' + bankAccountId + '/transfer';
            await httpClient.securePostAsync(url, request, jwt);
        }
        catch ( err ) {
            return this.handleApiError(err);
        }
    }

}
